import React, { useState, useEffect, useCallback } from "react";
// Material-UI imports
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Category as CategoryIcon,
  BarChart as StatsIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getCategoryStats,
} from "../../api/codeValues";
import { useAuth } from "../access/AuthContext";

const CategoryManagement = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [categoryStats, setCategoryStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Form states
  const [createForm, setCreateForm] = useState({
    categoryName: "",
    createdBy: "",
  });

  const [editForm, setEditForm] = useState({
    oldCategoryName: "",
    newCategoryName: "",
    updatedBy: "",
  });

  const [deleteForm, setDeleteForm] = useState({
    categoryName: "",
    deletedBy: "",
  });

  // Menu states
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuCategory, setMenuCategory] = useState(null);

  useEffect(() => {
    loadCategories();
    loadCategoryStats();
  }, []);

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await getAllCategories();
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryStats = async () => {
    setStatsLoading(true);
    try {
      const response = await getCategoryStats();
      setCategoryStats(response.data || {});
    } catch (error) {
      console.error("Error loading category stats:", error);
      toast.error("Failed to load category statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  const handleCreate = useCallback(async () => {
    try {
      await createCategory(createForm);
      toast.success("Category created successfully");
      setCreateDialogOpen(false);
      setCreateForm({ categoryName: "", createdBy: "" });
      loadCategories();
      loadCategoryStats();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(
        error.response?.data?.errorMessage || "Failed to create category"
      );
    }
  }, [createForm]);

  const handleEdit = useCallback(async () => {
    try {
      await updateCategory(editForm);
      toast.success("Category updated successfully");
      setEditDialogOpen(false);
      setSelectedCategory(null);
      setEditForm({ oldCategoryName: "", newCategoryName: "", updatedBy: "" });
      loadCategories();
      loadCategoryStats();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(
        error.response?.data?.errorMessage || "Failed to update category"
      );
    }
  }, [editForm]);

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteForm);
      toast.success("Category deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
      setDeleteForm({ categoryName: "", deletedBy: "" });
      loadCategories();
      loadCategoryStats();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(
        error.response?.data?.errorMessage || "Failed to delete category"
      );
    }
  };

  const handleMenuClick = (event, category) => {
    setAnchorEl(event.currentTarget);
    setMenuCategory(category);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuCategory(null);
  };

  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setEditForm({
      oldCategoryName: category,
      newCategoryName: category,
      updatedBy: "",
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = (category) => {
    setSelectedCategory(category);
    setDeleteForm({
      categoryName: category,
      deletedBy: "",
    });
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleStatsClick = (category) => {
    setSelectedCategory(category);
    setStatsDialogOpen(true);
    handleMenuClose();
  };

  const StatsDialog = () => {
    const categoryStatsData =
      selectedCategory && categoryStats.categories
        ? categoryStats.categories[selectedCategory]
        : null;

    return (
      <Dialog
        open={statsDialogOpen}
        onClose={() => setStatsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <StatsIcon sx={{ mr: 1 }} />
            Category Statistics
            {selectedCategory && (
              <Chip label={selectedCategory} sx={{ ml: 2 }} color="primary" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {statsLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedCategory ? (
            categoryStatsData ? (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="primary">
                        {categoryStatsData.totalEntries}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Total Entries
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="success.main">
                        {categoryStatsData.activeEntries}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Active Entries
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h4" color="error.main">
                        {categoryStatsData.inactiveEntries}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Inactive Entries
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              <Typography>
                No statistics available for this category.
              </Typography>
            )
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Overall Statistics
                </Typography>
                <Typography variant="h4" color="primary">
                  {categoryStats.totalCategories || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Categories
                </Typography>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Category Breakdown
                </Typography>
                {categoryStats.categories &&
                Object.keys(categoryStats.categories).length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Category</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Active</TableCell>
                          <TableCell align="right">Inactive</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(categoryStats.categories).map(
                          ([category, stats]) => (
                            <TableRow key={category}>
                              <TableCell>
                                <Chip label={category} size="small" />
                              </TableCell>
                              <TableCell align="right">
                                {stats.totalEntries}
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={stats.activeEntries}
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={stats.inactiveEntries}
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No category statistics available.
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: { xs: 0, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" }, // column on mobile, row on desktop
          justifyContent: { xs: "flex-start", md: "space-between" }, // start on mobile, space-between on desktop
          alignItems: { xs: "stretch", md: "center" }, // stretch on mobile, center on desktop
          gap: { xs: 1, md: 0 }, // spacing between items
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontSize: "22px" }}
            component="h1"
            gutterBottom
          >
            {/* <CategoryIcon sx={{ mr: 2, verticalAlign: "middle" }} /> */}
            Category Management
          </Typography>
          <Typography
            sx={{ fontSize: "10px" }}
            variant="body1"
            color="textSecondary"
          >
            Manage categories for organizing code/value pairs
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: { xs: "wrap", md: "nowrap" }, // wrap buttons on mobile
            mt: { xs: 1, md: 0 },
          }}
        >
          <Button
            variant="outlined"
            startIcon={<StatsIcon />}
            onClick={() => setStatsDialogOpen(true)}
            sx={{
              minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
              fontSize: { xs: "10px", md: "12px" },
            }}
          >
            View Statistics
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              minWidth: { xs: "120px", md: "auto" }, // smaller on mobile
              fontSize: { xs: "10px", md: "12px" },
            }}
          >
            Add New Category
          </Button>
        </Stack>
      </Box>

      {/* Categories Table */}
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Categories</Typography>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadCategories}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: "70vh" }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: "#17479e",
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    Category Name
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "#17479e",
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                  >
                    Statistics
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#17479e",
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No categories found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => {
                    const stats = categoryStats.categories?.[category];
                    return (
                      <TableRow key={category} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <CategoryIcon
                              sx={{ mr: 1, color: "text.secondary" }}
                            />
                            <Typography variant="body1" fontFamily="monospace">
                              {category}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {stats ? (
                            <Box display="flex" gap={1}>
                              <Chip
                                label={`${stats.totalEntries} total`}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={`${stats.activeEntries} active`}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No data
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            position: "sticky",
                            right: 0,
                            backgroundColor: "#fff",
                            zIndex: 1,
                          }}
                        >
                          <IconButton
                            onClick={(e) => handleMenuClick(e, category)}
                            size="small"
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditClick(menuCategory)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatsClick(menuCategory)}>
          <ListItemIcon>
            <StatsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Statistics</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteClick(menuCategory)}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create New Category Dialogs */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Category Name"
                value={createForm.categoryName}
                onChange={(e) =>
                  setCreateForm({ ...createForm, categoryName: e.target.value })
                }
                placeholder="e.g., PRIORITY_LEVEL"
                helperText="Use UPPER_CASE naming convention"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Created By"
                value={createForm.createdBy || user.email}
                onChange={(e) =>
                  setCreateForm({ ...createForm, createdBy: e.target.value })
                }
                placeholder={user.email}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!createForm.categoryName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
      {/* Editing Category dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Current Category Name"
                value={editForm.oldCategoryName}
                disabled
                helperText="This category will be renamed"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="New Category Name"
                value={editForm.newCategoryName}
                onChange={(e) =>
                  setEditForm({ ...editForm, newCategoryName: e.target.value })
                }
                placeholder="e.g., PRIORITY_TYPE"
                helperText="Use UPPER_CASE naming convention"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Updated By"
                value={editForm.updatedBy || user.email}
                onChange={(e) =>
                  setEditForm({ ...editForm, updatedBy: e.target.value })
                }
                placeholder={user.email}
              />
            </Grid>
          </Grid>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> Renaming a category will update all
              associated code/value entries. This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEdit}
            variant="contained"
            disabled={
              !editForm.newCategoryName.trim() ||
              editForm.newCategoryName === editForm.oldCategoryName
            }
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>
      {/* Delete Category dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to delete this category? This will deactivate
            all associated code/value entries.
          </Typography>
          <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2">
              Category: {selectedCategory}
            </Typography>
          </Box>
          <TextField
            size="small"
            fullWidth
            label="Deleted By"
            value={deleteForm.deletedBy}
            onChange={(e) =>
              setDeleteForm({ ...deleteForm, deletedBy: e.target.value })
            }
            placeholder={user.email}
          />
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action will deactivate all
              code/value entries in this category. This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={!deleteForm.deletedBy.trim()}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <StatsDialog />
    </Box>
  );
};

export default CategoryManagement;
